import { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { CalendarClock, X } from "lucide-react";
import ScheduledMessageDialog from "./ScheduledMessageDialog";
import { formatDate } from "./TextMessageCompose";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";
import { StyledMenu } from "../common/DropdownMenu";
import { ScheduleMessageReturnType } from "./useScheduleMessage";

export const StaticByline = ({ scheduledTime }: { scheduledTime: bigint }) => {
  return (
    <Box
      sx={{
        padding: "4px 8px",
      }}
    >
      <Typography
        component="div"
        sx={{
          color: "#198282",
          fontSize: "14px",
          gap: "8px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
        }}
      >
        <CalendarClock size={20} />
        Scheduled for {formatDate(new Date(Number(scheduledTime) * 1000))}
      </Typography>
    </Box>
  );
};

const _MaybeBoxed = ({
  boxed,
  children,
  onClick,
}: {
  boxed: boolean;
  children: React.ReactNode | React.ReactNode[];
  onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}) => {
  if (!boxed) {
    return <>{children}</>;
  }
  return (
    <Box
      sx={(theme) => ({
        padding: "16px",
        borderRadius: "8px",
        gap: "8px",
        border: `1px solid ${theme.palette.border}`,
        position: "relative",
        cursor: "pointer",
        alignItems: "center",
        marginBottom: "8px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      })}
    >
      {children}
      <IconButton onClick={onClick}>
        <X size={20} />
      </IconButton>
    </Box>
  );
};

export default ({
  scheduledTime,
  scheduledManager,
  boxed = false,
}: {
  scheduledTime: bigint;
  scheduledManager: ScheduleMessageReturnType;
  boxed?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [scheduled, setScheduled] = useState<Date>(
    new Date(Number(scheduledTime) * 1000),
  );
  return (
    <_MaybeBoxed boxed={boxed} onClick={handleClick}>
      <Box
        sx={{
          padding: "4px 8px",
        }}
      >
        <Button
          variant={"text"}
          sx={{
            height: "unset",
            fontSize: "14px",
          }}
          onClick={handleClick}
          startIcon={<CalendarClock size={20} />}
        >
          Scheduled for {formatDate(scheduled)}
        </Button>
        <StyledMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <MenuItem
            disabled={scheduledManager.loading}
            onClick={async () => {
              await scheduledManager.handleSendNow();
              setAnchorEl(null);
            }}
            disableRipple
            sx={{
              gap: "12px",
              height: "40px",
            }}
          >
            Send now
          </MenuItem>
          <MenuItem
            disabled={scheduledManager.loading}
            onClick={() => {
              setAnchorEl(null);
              setSchedulerOpen(true);
            }}
            disableRipple
            sx={{
              gap: "12px",
              height: "40px",
            }}
          >
            Change time
          </MenuItem>
          <MenuItem
            disabled={scheduledManager.loading}
            onClick={async () => {
              await scheduledManager.handleDelete();
              setAnchorEl(null);
            }}
            disableRipple
            sx={{
              gap: "12px",
              height: "40px",
            }}
          >
            Delete
          </MenuItem>
        </StyledMenu>
        {/* Drop down */}
        <ScheduledMessageDialog
          open={schedulerOpen}
          value={scheduled}
          onUpdate={async (d) => {
            await scheduledManager.handleUpdateSchedule(d);
            setScheduled(d);
          }}
          onClose={() => {
            setSchedulerOpen(false);
          }}
        />
      </Box>
    </_MaybeBoxed>
  );
};
